import React from "react";
import {
  Grid,
  Box,
  Typography,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";
import getHistorics, { getPointsOfSales } from "lib/requests/historics";
import { resampleHistoricsToMonthly } from "lib/utils/historics";
import { useEffectAsync } from "lib/react/customHooks";
import { useSelector, useDispatch } from "react-redux";
import { primaryColor } from "resources/constants";
import ScaleLoader from "react-spinners/ScaleLoader";
import _ from "lodash";
import moment from "moment";
import mixpanelTrack from "app/common/MixpanelComponent";
import { initialFiltersKey } from "app/filters/duck/slices";
import HistorySummaryComponent from "./summary/HistorySummaryComponent";
import HistoryAggregateComponent from "./aggregate/HistoryAggregateComponent";
import HistoryPassthroughComponent from "./passthrough/HistoryPassthroughComponent";
import HistoryMarginComponent from "./margin/HistoryMarginComponent";
import HistoryVolumeComponent from "./volume/HistoryVolumeComponent";
import { setTupleData } from "./duck/slices";

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up("lg")]: {
      overflow: "hidden",
      flexGrow: "1",
    },
  },
  headerContainer: {
    display: "flex",
    borderBottom: "1px solid #e2e2e2",
    marginBottom: "10px",
    marginLeft: "10px",
    marginRight: "10px",
    flexWrap: "nowrap",
    [theme.breakpoints.down("xs")]: {
      flexWrap: "wrap",
      paddingBottom: "10px",
    },
  },
  headerTitle: {
    paddingBottom: theme.spacing(1.5),
  },
  spinnerContainer: {
    display: "flex",
    flexGrow: "1",
    alignItems: "center",
    justifyContent: "center",
  },
  halfHeight: theme.halfHeight,
}));

function HistoryComponent() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const filtersSlice = useSelector((state) => state.filters);
  const historicsSlice = useSelector((state) => state.historics);
  const dataloadSlice = useSelector((state) => state.dataload);
  const [
    filteredAggregatedHistorics,
    setFilteredAggregatedHistorics,
  ] = React.useState(null);
  const [filterHistoricsDataYoY, setFilterHistoricsDataYoY] = React.useState(
    []
  );

  const [pointsOfSalesData, setPointsOfSalesData] = React.useState(null);
  const [scopeSummaryData, setScopeSummaryData] = React.useState(null);
  const NUMBER_OF_HISTORIC_MONTHS = 24;

  const retrieveHistoryForTuple = async (tupleKey) => {
    if (historicsSlice.tuples[tupleKey]) {
      return {
        key: tupleKey,
        historics: historicsSlice.tuples[tupleKey].historics,
        pointsOfSales: historicsSlice.tuples[tupleKey].pointsOfSales,
        scopeSummary: historicsSlice.tuples[tupleKey].scopeSummary,
        historicsAfterOneYear:
          historicsSlice.tuples[tupleKey].historicsAfterOneYear,
      };
    }

    const queryFilters = {
      ...filtersSlice.queryFilters,
      is_product_client: 1,
      data_source_id: 1,
    };

    // Historics can now come back at daily/weekly cadence instead of a fixed
    // one-row-per-month cadence, so we can't rely on `limit: [N]` to mean "N
    // months" anymore. We first find the latest available date, then fetch
    // by date range (generous raw-row limit to cover daily granularity) and
    // resample to one row per calendar month before doing anything else.
    const maxHistoricDateResult = await getHistorics({
      filters: queryFilters,
      group_by: "date",
      order_by: ["date"],
      order_direction: ["desc"],
      limit: [1],
    });

    const maxHistoricDate = maxHistoricDateResult.data[0]?.date;

    const historicsResult = maxHistoricDate
      ? await getHistorics({
          filters: queryFilters,
          group_by: "date",
          sum: [
            "margin",
            "units",
            "units_in",
            "sales",
            "sales_in",
            "retailer_margin",
          ],
          sum_boolean: ["is_promotion", "is_followed"],
          avg_weight_by_units: ["cost", "price", "price_in"],
          min_date: moment(maxHistoricDate)
            .subtract(NUMBER_OF_HISTORIC_MONTHS, "months")
            .format("YYYY-MM-DD"),
          max_date: maxHistoricDate,
          order_by: ["date"],
          order_direction: ["desc"],
          limit: [NUMBER_OF_HISTORIC_MONTHS * 31],
        })
      : { data: [] };

    const monthlyHistorics = _.orderBy(
      resampleHistoricsToMonthly(historicsResult.data),
      "date",
      "desc"
    );

    let historics = [];
    if (monthlyHistorics.length > 11) {
      historics = monthlyHistorics.slice(0, 12);
      setFilterHistoricsDataYoY(monthlyHistorics.slice(12, 24));
    } else {
      historics = monthlyHistorics;
    }

    const tupleData = {
      key: tupleKey,
      historics,
      pointsOfSales: [],
      scopeSummary: {},
    };

    if (!historics.length) return tupleData;

    const lastHistoricsDate = historics[0].date;
    const minHistoricDate = historics[historics.length - 1].date;

    const scopeForSummaryFilters = {
      filters: queryFilters,
      distinct_count: ["retailer_id", "area_id", "product_id"],
      max_date: lastHistoricsDate,
      min_date: minHistoricDate,
    };

    const groupByFilters = _.omit(
      queryFilters,
      "is_product_client",
      "data_source_id"
    );
    const pointOfSalesQueryFilters = _.omit(queryFilters, "data_source_id");

    const pointsOfSalesByDateFilters = {
      filters: pointOfSalesQueryFilters,
      group_by: ["date", ...Object.keys(groupByFilters)],
      distinct_count: ["point_of_sale_id"],
      max_date: lastHistoricsDate,
      min_date: minHistoricDate,
    };

    const [
      scopeForSummaryResult,
      pointsOfSalesByDateResult,
    ] = await Promise.all([
      getHistorics(scopeForSummaryFilters),
      getPointsOfSales(true, pointsOfSalesByDateFilters),
    ]);

    return {
      ...tupleData,
      pointsOfSales: pointsOfSalesByDateResult.data,
      scopeSummary: scopeForSummaryResult.data && scopeForSummaryResult.data[0],
      historicsAfterOneYear: monthlyHistorics.slice(12, 24),
    };
  };

  useEffectAsync(
    async () => {
      const totalsTupleKey = initialFiltersKey;
      const tupleKey = filtersSlice.filtersKey;

      // If we come from a different module with filters already implemented we need to request the global historics before to be able to perform comparisons
      if (
        tupleKey !== totalsTupleKey &&
        !historicsSlice.tuples[totalsTupleKey]
      ) {
        const totalsHistory = await retrieveHistoryForTuple(totalsTupleKey);
        dispatch(
          setTupleData({
            [totalsTupleKey]: {
              historics: totalsHistory.historics,
              pointsOfSales: totalsHistory.pointsOfSales,
              scopeSummary: totalsHistory.scopeSummary,
              historicsAfterOneYear: totalsHistory.historicsAfterOneYear,
            },
          })
        );
      }

      return retrieveHistoryForTuple(tupleKey);
    },
    (result) => {
      const orderedHistorics = _.orderBy(result.historics, "date", "asc");
      const orderedPointsOfSales = _.orderBy(
        result.pointsOfSales,
        "date",
        "asc"
      );
      dispatch(
        setTupleData({
          [result.key]: {
            historics: orderedHistorics,
            pointsOfSales: orderedPointsOfSales,
            scopeSummary: result.scopeSummary,
            historicsAfterOneYear: result.historicsAfterOneYear,
          },
        })
      );
      setFilteredAggregatedHistorics(orderedHistorics);
      setPointsOfSalesData(orderedPointsOfSales);
      setScopeSummaryData(result.scopeSummary);
    },
    [filtersSlice.filters]
  );

  React.useEffect(() => {
    /* MIXPANEL TRACKS */
    mixpanelTrack("History module visited");
    /* END OF MIXPANEL TRACKS */
  }, []);

  return (
    <>
      {dataloadSlice.selectedDataload.id && (
        <>
          <Box className={classes.headerContainer}>
            <Typography variant="h6" className={classes.headerTitle}>
              History & Passthrough
            </Typography>
          </Box>
          {filteredAggregatedHistorics && scopeSummaryData ? (
            <Grid container className={classes.root}>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={3}
                className={!isSmallScreen && classes.halfHeight}
              >
                <HistorySummaryComponent scopeSummaryData={scopeSummaryData} />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={3}
                className={!isSmallScreen && classes.halfHeight}
              >
                <HistoryAggregateComponent
                  historics={filteredAggregatedHistorics}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={6}
                className={!isSmallScreen && classes.halfHeight}
              >
                <HistoryPassthroughComponent
                  historics={filteredAggregatedHistorics}
                  historicsDataYoY={
                    _.isEmpty(filterHistoricsDataYoY)
                      ? historicsSlice.tuples[filtersSlice.filtersKey] &&
                        historicsSlice.tuples[filtersSlice.filtersKey]
                          .historicsAfterOneYear
                      : filterHistoricsDataYoY
                  }
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={6}
                className={!isSmallScreen && classes.halfHeight}
              >
                <HistoryMarginComponent
                  historics={filteredAggregatedHistorics}
                  historicsDataYoY={
                    _.isEmpty(filterHistoricsDataYoY)
                      ? historicsSlice.tuples[filtersSlice.filtersKey] &&
                        historicsSlice.tuples[filtersSlice.filtersKey]
                          .historicsAfterOneYear
                      : filterHistoricsDataYoY
                  }
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={6}
                className={!isSmallScreen && classes.halfHeight}
              >
                {pointsOfSalesData && (
                  <HistoryVolumeComponent
                    historics={filteredAggregatedHistorics}
                    salesPoints={pointsOfSalesData}
                    historicsDataYoY={
                      _.isEmpty(filterHistoricsDataYoY)
                        ? historicsSlice.tuples[filtersSlice.filtersKey] &&
                          historicsSlice.tuples[filtersSlice.filtersKey]
                            .historicsAfterOneYear
                        : filterHistoricsDataYoY
                    }
                  />
                )}
              </Grid>
            </Grid>
          ) : (
            <Box component="div" className={classes.spinnerContainer}>
              <ScaleLoader size={150} color={primaryColor} loading />
            </Box>
          )}
        </>
      )}
    </>
  );
}

export default HistoryComponent;
